import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Mail, Edit, Save, X, Info } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmailTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ subject: '', body: '' });

  const templateLabels = {
    'booking_confirmation': 'Booking Confirmation',
    'order_pickup_scheduled': 'Order Pickup Scheduled',
    'order_in_progress': 'Order In Progress',
    'order_completed': 'Order Completed',
    'order_delivered': 'Order Delivered',
    'custom_notification': 'Custom Notification'
  };

  const templateDescriptions = {
    'booking_confirmation': 'Sent when a customer books a service',
    'order_pickup_scheduled': 'Sent when vehicle pickup is scheduled',
    'order_in_progress': 'Sent when service work is ongoing',
    'order_completed': 'Sent when service is completed',
    'order_delivered': 'Sent when vehicle is delivered back',
    'custom_notification': 'For custom messages to customers'
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/email-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Set empty templates array and show a more informative error
      setTemplates([]);
      toast({
        title: 'Error',
        description: `Failed to load email templates: ${error.response?.status || 'Network error'}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      subject: template.subject,
      body: template.body
    });
    setIsEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.subject || !formData.body) {
      toast({
        title: 'Validation Error',
        description: 'Subject and body are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/email-templates/${editingTemplate.template_type}`,
        formData
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Email template updated successfully'
        });
        setIsEditDialogOpen(false);
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update template',
        variant: 'destructive'
      });
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.querySelector('textarea[name="body"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.body;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + `{${variable}}` + after;
      
      setFormData({ ...formData, body: newText });
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length + 2, start + variable.length + 2);
      }, 0);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            <div>
              <CardTitle>Email Templates Management</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Customize email templates sent to customers for bookings and orders
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-lg transition-all">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {templateLabels[template.template_type] || template.template_type}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {templateDescriptions[template.template_type] || 'Custom email template'}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleEdit(template)}
                  className="flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Subject:</p>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {template.subject}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Variables Available:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.variables && template.variables.map((variable, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                      >
                        {`{${variable}}`}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Preview:</p>
                  <div className="text-xs text-gray-700 bg-gray-50 p-3 rounded max-h-32 overflow-y-auto whitespace-pre-wrap">
                    {template.body.substring(0, 200)}...
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Email Template: {editingTemplate && (templateLabels[editingTemplate.template_type] || editingTemplate.template_type)}
            </DialogTitle>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-4">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">How to use variables:</p>
                  <p>Click on a variable below to insert it at cursor position. Variables will be replaced with actual data when email is sent.</p>
                  <p className="mt-2"><strong>Example:</strong> {`{customer_name}`} → "John Doe"</p>
                </div>
              </div>

              {/* Available Variables */}
              <div>
                <label className="block text-sm font-medium mb-2">Available Variables:</label>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.variables && editingTemplate.variables.map((variable, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => insertVariable(variable)}
                      className="text-xs"
                    >
                      {`{${variable}}`}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium mb-2">Email Subject:</label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Enter email subject"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium mb-2">Email Body:</label>
                <Textarea
                  name="body"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Enter email body content"
                  rows={15}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use variables like {`{customer_name}`} in your template. They will be replaced with actual values.
                </p>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium mb-2">Preview:</label>
                <div className="bg-gray-50 border rounded p-4 max-h-64 overflow-y-auto">
                  <div className="text-sm font-semibold mb-2 text-gray-900">
                    Subject: {formData.subject}
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">
                    {formData.body}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesManager;
